import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { FINANCIALS } from '../../data/content';
import SectionHeader from '../SectionHeader';
import Callout from '../Callout';
import { useIntersection } from '../../hooks/useIntersection';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const G2 = '#2F9E5C';
const G5 = '#A8D5B5';
const GR = '#6B7280';
const BL = '#1565C0';
const GRID = '#E2ECE9';

const baseOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false } },
};

const tickStyle = { color: GR, font: { size: 11, family: 'Inter' } };

export default function Financials() {
  const { ref, visible } = useIntersection();

  const revData = {
    labels: FINANCIALS.chartData.revenue.labels,
    datasets: [{
      data: FINANCIALS.chartData.revenue.data,
      backgroundColor: [G5, G5, G2, BL],
      borderRadius: 6, borderSkipped: false,
    }],
  };

  const marginData = {
    labels: FINANCIALS.chartData.margin.labels,
    datasets: [{
      data: FINANCIALS.chartData.margin.data,
      borderColor: G2, borderWidth: 3,
      pointBackgroundColor: G2, pointRadius: 6,
      fill: true, backgroundColor: 'rgba(47,158,92,0.07)', tension: 0.4,
    }],
  };

  const revOpts = {
    ...baseOpts,
    scales: {
      x: { grid: { display: false }, ticks: tickStyle },
      y: { grid: { color: GRID }, ticks: { ...tickStyle, callback: v => v >= 1000000 ? 'AED ' + (v / 1000000).toFixed(1) + 'M' : 'AED ' + (v / 1000) + 'K' } },
    },
  };

  const marginOpts = {
    ...baseOpts,
    scales: {
      x: { grid: { display: false }, ticks: tickStyle },
      y: { grid: { color: GRID }, ticks: { ...tickStyle, callback: v => v + '%' }, min: 0, max: 50 },
    },
  };

  return (
    <section id="financials" style={{ padding: '72px 0', borderBottom: '1px solid #D1D5DB', background: 'var(--gray-lt)' }}>
      <div className="r25-section-wrap" style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '0 40px' }}>
        <SectionHeader num={FINANCIALS.sectionNum} title={FINANCIALS.title} titleEm={FINANCIALS.titleEm} body={FINANCIALS.body} />
        <div ref={ref} className={`fade-in${visible ? ' visible' : ''}`}>
          <div className="r25-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
            {[
              { title: 'Annual Revenue Growth (AED)', chart: <Bar data={revData} options={revOpts} /> },
              { title: 'Gross Margin Improvement', chart: <Line data={marginData} options={marginOpts} /> },
            ].map(({ title, chart }) => (
              <div key={title} className="r25-chart-wrap" style={{ background: '#fff', borderRadius: 8, padding: 24, border: '1px solid #D1D5DB' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 16 }}>{title}</div>
                <div style={{ position: 'relative', height: 220 }}>{chart}</div>
              </div>
            ))}
          </div>

          <div className="r25-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 12px rgba(0,0,0,0.05)' }}>
              <thead>
                <tr>
                  {['Metric', '2024 Actual', '2025 Actual', '2026 Target'].map((h, i) => (
                    <th key={h} style={{ background: 'var(--g2)', color: '#fff', padding: '13px 16px', fontWeight: 600, fontSize: 12, textAlign: i === 0 ? 'left' : 'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FINANCIALS.scorecard.map((row, i) => (
                  <tr key={i}>
                    {[row.metric, row.y2024, row.y2025, row.y2026].map((cell, j) => (
                      <td key={j} style={{
                        padding: '11px 16px',
                        borderBottom: '1px solid #D1D5DB',
                        background: row.highlight ? 'var(--gpale)' : (i % 2 === 0 ? '#fff' : 'var(--gray-lt)'),
                        color: j === 0 ? 'var(--ink)' : (j === 3 ? BL : 'var(--gray)'),
                        textAlign: j === 0 ? 'left' : 'center',
                        fontWeight: row.highlight && j === 0 ? 700 : 400,
                      }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Callout title={FINANCIALS.callout.title}>
            {FINANCIALS.callout.points.map((p, i) => (
              <p key={i} style={{ marginBottom: i < FINANCIALS.callout.points.length - 1 ? 6 : 0 }}>{p}</p>
            ))}
          </Callout>
          <p style={{ fontSize: 14, color: 'var(--gray)', fontStyle: 'italic' }}>{FINANCIALS.note}</p>
        </div>
      </div>
    </section>
  );
}
